/**
 * The chart's indicator + pane model (pure, JSON in / JSON out). The chart panel keeps a
 * `ChartIndicatorsState` in its params (persisted with the layout, synced with the workspace);
 * every UI action is one of these functions, and the render worker receives the result.
 *
 * Panes (TradingView model, docs/TRADINGVIEW-REFERENCE.md §1): the price pane (`main`, holding
 * candles + the built-in volume strip + overlays) and N sub-panes below it, each with a relative
 * height (`weight`), collapsible, one pane maximisable. A sub-pane exists only while it holds an
 * indicator: removing or moving out its last indicator deletes it.
 */
import type { BarSpec } from "../bars/barBuilder.ts";
import { parseSourceRef, sourceRef } from "./context.ts";
import type { AnyIndicatorDef } from "./define.ts";
import { resolveParams, type IndicatorRegistry } from "./registry.ts";
import {
  MAIN_PANE,
  VISIBILITY_UNITS,
  type ChartIndicatorsState,
  type IndicatorInstance,
  type IndicatorTemplate,
  type InstanceStyle,
  type PaneState,
  type Visibility,
  type VisibilityUnit,
} from "./types.ts";

/** SPEC §12.4: maximum 25 indicators per chart. */
export const MAX_INDICATORS = 25;
/** Sub-panes per chart. */
export const MAX_PANES = 8;
/** Default relative height of the price pane vs a sub-pane (1). */
export const DEFAULT_MAIN_WEIGHT = 3;
/** Minimum pane height (CSS px) while dragging a separator (LWC uses 30). */
export const MIN_PANE_PX = 30;
/** Height of a collapsed pane: its legend row. */
export const COLLAPSED_PANE_PX = 22;

export function emptyIndicatorsState(): ChartIndicatorsState {
  return { v: 1, instances: [], panes: [], mainWeight: DEFAULT_MAIN_WEIGHT, maximized: null };
}

/** `ema-1`, `ema-2` … : the lowest free number for the type. */
export function nextInstanceId(state: ChartIndicatorsState, type: string): string {
  const used = new Set(state.instances.map((x) => x.id));
  for (let n = 1; ; n++) {
    const id = `${type}-${String(n)}`;
    if (!used.has(id)) return id;
  }
}

function nextPaneId(state: ChartIndicatorsState): string {
  const used = new Set(state.panes.map((p) => p.id));
  for (let n = 1; ; n++) {
    const id = `pane-${String(n)}`;
    if (!used.has(id)) return id;
  }
}

/** Drop sub-panes that hold no indicator (and a maximised pane that no longer exists). */
function prunePanes(state: ChartIndicatorsState): ChartIndicatorsState {
  const used = new Set(state.instances.map((x) => x.pane));
  const panes = state.panes.filter((p) => used.has(p.id));
  const maximized =
    state.maximized === MAIN_PANE || panes.some((p) => p.id === state.maximized) ? state.maximized : null;
  return { ...state, panes, maximized };
}

/** `auto`, `main`, `new` or an existing sub-pane id. */
export type AddTarget = string;

/**
 * Add an instance of `def`: `auto` = the definition's default (overlay → price pane, else a new
 * pane at the bottom). Returns the unchanged state (and `id` null) at the indicator limit.
 */
export function addIndicator(
  state: ChartIndicatorsState,
  def: AnyIndicatorDef,
  opts: { params?: Record<string, unknown>; target?: AddTarget } = {},
): { state: ChartIndicatorsState; id: string | null } {
  if (state.instances.length >= MAX_INDICATORS) return { state, id: null };
  const id = nextInstanceId(state, def.id);
  const target = opts.target ?? "auto";
  let panes = state.panes;
  let pane: string;
  if (target === "main" || (target === "auto" && def.overlay)) pane = MAIN_PANE;
  else if (target !== "auto" && target !== "new" && panes.some((p) => p.id === target)) pane = target;
  else if (panes.length >= MAX_PANES) pane = panes[panes.length - 1]?.id ?? MAIN_PANE;
  else {
    pane = nextPaneId(state);
    panes = [...panes, { id: pane, weight: 1 }];
  }
  const inst: IndicatorInstance = {
    id,
    type: def.id,
    params: resolveParams(def, opts.params ?? {}),
    pane,
    visible: true,
  };
  return { state: { ...state, panes, instances: [...state.instances, inst] }, id };
}

/**
 * Remove an instance. Indicators that used one of its plots as their source fall back to their
 * default source (TradingView keeps the dependent and resets the input). Its pane goes when empty.
 */
export function removeIndicator(
  state: ChartIndicatorsState,
  id: string,
  registry: IndicatorRegistry,
): ChartIndicatorsState {
  const instances = state.instances
    .filter((x) => x.id !== id)
    .map((x) => {
      const def = registry.get(x.type);
      if (!def) return x;
      let params = x.params;
      for (const [k, spec] of Object.entries(def.inputs)) {
        const v = params[k];
        if (spec.type === "source" && typeof v === "string" && parseSourceRef(v)?.instanceId === id)
          params = { ...params, [k]: spec.default };
      }
      return params === x.params ? x : { ...x, params };
    });
  return prunePanes({ ...state, instances });
}

/**
 * Move an instance to the price pane (`main`), a new pane (`new`: directly below its current
 * pane, or at the bottom when it came from the price pane), or an existing pane id.
 */
export function moveIndicator(state: ChartIndicatorsState, id: string, target: string): ChartIndicatorsState {
  const inst = state.instances.find((x) => x.id === id);
  if (!inst) return state;
  let panes = state.panes;
  let pane = target;
  if (target === "new") {
    if (panes.length >= MAX_PANES) return state;
    pane = nextPaneId(state);
    const at = panes.findIndex((p) => p.id === inst.pane);
    const fresh: PaneState = { id: pane, weight: 1 };
    panes = at < 0 ? [...panes, fresh] : [...panes.slice(0, at + 1), fresh, ...panes.slice(at + 1)];
  } else if (target !== MAIN_PANE && !panes.some((p) => p.id === target)) {
    return state;
  }
  if (pane === inst.pane) return state;
  const instances = state.instances.map((x) => (x.id === id ? { ...x, pane } : x));
  return prunePanes({ ...state, panes, instances });
}

/** Delete a sub-pane and every indicator in it. */
export function removePane(
  state: ChartIndicatorsState,
  paneId: string,
  registry: IndicatorRegistry,
): ChartIndicatorsState {
  if (paneId === MAIN_PANE) return state;
  let s = state;
  for (const x of state.instances) if (x.pane === paneId) s = removeIndicator(s, x.id, registry);
  return prunePanes({ ...s, panes: s.panes.filter((p) => p.id !== paneId) });
}

/** Move a sub-pane up (−1) or down (+1) among the sub-panes. */
export function movePane(state: ChartIndicatorsState, paneId: string, dir: -1 | 1): ChartIndicatorsState {
  const i = state.panes.findIndex((p) => p.id === paneId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= state.panes.length) return state;
  const panes = [...state.panes];
  const a = panes[i];
  const b = panes[j];
  if (!a || !b) return state;
  panes[i] = b;
  panes[j] = a;
  return { ...state, panes };
}

export function setPaneCollapsed(
  state: ChartIndicatorsState,
  paneId: string,
  collapsed: boolean,
): ChartIndicatorsState {
  const panes = state.panes.map((p) => (p.id === paneId ? { ...p, collapsed } : p));
  return { ...state, panes, maximized: collapsed && state.maximized === paneId ? null : state.maximized };
}

/** Collapse the price pane's indicator legend to its first row, or expand it (absent = expanded). */
export function setLegendCollapsed(state: ChartIndicatorsState, collapsed: boolean): ChartIndicatorsState {
  const { legendCollapsed: _was, ...rest } = state;
  return collapsed ? { ...rest, legendCollapsed: true } : rest;
}

/** Maximise a pane (every other pane hidden), or restore when it already is. */
export function toggleMaximize(state: ChartIndicatorsState, paneId: string): ChartIndicatorsState {
  if (state.maximized === paneId) return { ...state, maximized: null };
  if (paneId !== MAIN_PANE && !state.panes.some((p) => p.id === paneId)) return state;
  const panes = state.panes.map((p) => (p.id === paneId ? { ...p, collapsed: false } : p));
  return { ...state, panes, maximized: paneId };
}

export function updateInstance(
  state: ChartIndicatorsState,
  id: string,
  patch: {
    params?: Record<string, unknown>;
    style?: InstanceStyle;
    visible?: boolean;
    visibility?: Visibility;
  },
  registry?: IndicatorRegistry,
): ChartIndicatorsState {
  const instances = state.instances.map((x) => {
    if (x.id !== id) return x;
    const def = registry?.get(x.type);
    const next = { ...x, ...patch };
    if (patch.params && def) next.params = resolveParams(def, patch.params);
    return next;
  });
  return { ...state, instances };
}

// ------------------------------------------------------------------ geometry

export type PaneBox = {
  id: string;
  /** CSS px from the top of the plot area. */
  top: number;
  height: number;
  collapsed: boolean;
  /** Hidden because another pane is maximised. */
  hidden: boolean;
};

/** Pane ids top to bottom (`main` first). */
export function paneOrder(state: ChartIndicatorsState): string[] {
  return [MAIN_PANE, ...state.panes.map((p) => p.id)];
}

/**
 * Stack the panes into `totalPx` (the plot height above the time axis): a maximised pane takes it
 * all; collapsed panes take `COLLAPSED_PANE_PX`; the rest share what is left by weight (integer
 * px, the last visible pane takes the rounding remainder).
 */
export function paneLayout(state: ChartIndicatorsState, totalPx: number): PaneBox[] {
  const ids = paneOrder(state);
  const H = Math.max(0, Math.floor(totalPx));
  if (state.maximized !== null && ids.includes(state.maximized)) {
    let top = 0;
    return ids.map((id) => {
      const on = id === state.maximized;
      const box = { id, top, height: on ? H : 0, collapsed: false, hidden: !on };
      top += box.height;
      return box;
    });
  }
  const meta = ids.map((id) => {
    if (id === MAIN_PANE) return { id, weight: state.mainWeight, collapsed: false };
    const p = state.panes.find((q) => q.id === id);
    return { id, weight: p?.weight ?? 1, collapsed: p?.collapsed === true };
  });
  const collapsedPx = meta.filter((m) => m.collapsed).length * COLLAPSED_PANE_PX;
  const free = Math.max(0, H - collapsedPx);
  const open = meta.filter((m) => !m.collapsed);
  const wsum = open.reduce((a, m) => a + (m.weight > 0 ? m.weight : 1), 0) || 1;
  const lastOpen = open[open.length - 1]?.id;
  let used = 0;
  const heights = new Map<string, number>();
  for (const m of open) {
    if (m.id === lastOpen) continue;
    const h = Math.floor((free * (m.weight > 0 ? m.weight : 1)) / wsum);
    heights.set(m.id, h);
    used += h;
  }
  if (lastOpen !== undefined) heights.set(lastOpen, Math.max(0, free - used));
  let top = 0;
  return meta.map((m) => {
    const height = m.collapsed ? COLLAPSED_PANE_PX : (heights.get(m.id) ?? 0);
    const box = { id: m.id, top, height, collapsed: m.collapsed, hidden: false };
    top += height;
    return box;
  });
}

/**
 * Drag the separator above pane `belowId` by `dyPx` (positive = down). Heights come from
 * `paneLayout(state, totalPx)`; the two neighbouring open panes trade height (each keeps at least
 * `MIN_PANE_PX`) and every open pane's weight becomes its pixel height, so proportions survive a
 * resize of the chart.
 */
export function resizePanes(
  state: ChartIndicatorsState,
  belowId: string,
  dyPx: number,
  totalPx: number,
): ChartIndicatorsState {
  const boxes = paneLayout(state, totalPx);
  const k = boxes.findIndex((b) => b.id === belowId);
  const below = boxes[k];
  // The nearest open pane above the separator.
  let a = k - 1;
  while (a >= 0 && boxes[a]?.collapsed) a--;
  const above = boxes[a];
  if (!below || !above || k <= 0 || below.collapsed || below.hidden || above.hidden) return state;
  const room = above.height + below.height;
  const newAbove = Math.min(Math.max(above.height + dyPx, MIN_PANE_PX), room - MIN_PANE_PX);
  if (room < 2 * MIN_PANE_PX) return state;
  const h = new Map(boxes.map((b) => [b.id, b.height]));
  h.set(above.id, newAbove);
  h.set(below.id, room - newAbove);
  const weightOf = (id: string, old: number): number => Math.max(1, h.get(id) ?? old);
  return {
    ...state,
    mainWeight: weightOf(MAIN_PANE, state.mainWeight),
    panes: state.panes.map((p) => (p.collapsed ? p : { ...p, weight: weightOf(p.id, p.weight) })),
  };
}

// ------------------------------------------------------------------ visibility

/** The Visibility-tab unit a bar spec falls under. */
export function visibilityUnit(spec: BarSpec): VisibilityUnit {
  switch (spec.kind) {
    case "time":
      return spec.periodMs < 60_000 ? "seconds" : spec.periodMs < 3_600_000 ? "minutes" : "hours";
    case "session":
      return spec.unit === "day" ? "days" : spec.unit === "week" ? "weeks" : "months";
    case "tick":
      return "ticks";
    case "volume":
      return "volume";
    case "range":
      return "range";
    case "renko":
      return "renko";
  }
}

/** Drawn on this chart? (eye on, and the timeframe unit not switched off). */
export function isShownOn(inst: IndicatorInstance, spec: BarSpec): boolean {
  return inst.visible && inst.visibility?.[visibilityUnit(spec)] !== false;
}

// ------------------------------------------------------------------ persistence

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

function sanitizeStyle(raw: unknown): InstanceStyle | undefined {
  if (!isRecord(raw)) return undefined;
  const out: InstanceStyle = {};
  const pick = (r: unknown): Record<string, Record<string, unknown>> => {
    const o: Record<string, Record<string, unknown>> = {};
    if (!isRecord(r)) return o;
    for (const [k, v] of Object.entries(r)) {
      if (!isRecord(v)) continue;
      const e: Record<string, unknown> = {};
      if (typeof v["visible"] === "boolean") e["visible"] = v["visible"];
      if (typeof v["color"] === "string" && /^#([0-9a-f]{6}|[0-9a-f]{8})$/i.test(v["color"]))
        e["color"] = v["color"];
      if (typeof v["width"] === "number" && v["width"] >= 0.5 && v["width"] <= 8) e["width"] = v["width"];
      if (v["style"] === "solid" || v["style"] === "dashed" || v["style"] === "dotted")
        e["style"] = v["style"];
      if (typeof v["alpha"] === "number" && v["alpha"] >= 0 && v["alpha"] <= 1) e["alpha"] = v["alpha"];
      o[k] = e;
    }
    return o;
  };
  if (raw["plots"] !== undefined) out.plots = pick(raw["plots"]);
  if (raw["fills"] !== undefined) out.fills = pick(raw["fills"]);
  if (typeof raw["legendValues"] === "boolean") out.legendValues = raw["legendValues"];
  return out;
}

function sanitizeVisibility(raw: unknown): Visibility | undefined {
  if (!isRecord(raw)) return undefined;
  const out: Visibility = {};
  for (const u of VISIBILITY_UNITS) if (raw[u] === false) out[u] = false;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Parse stored params (`unknown`, from a layout) into a valid state: malformed entries are
 * dropped, duplicate ids renamed away, params sanitised against known definitions (unknown types
 * are kept verbatim so a newer layout survives an older build), orphan panes pruned and
 * instances pointing at a missing pane placed by their definition's default.
 */
export function sanitizeIndicatorsState(raw: unknown, registry: IndicatorRegistry): ChartIndicatorsState {
  const base = emptyIndicatorsState();
  if (!isRecord(raw) || raw["v"] !== 1) return base;
  const panes: PaneState[] = [];
  if (Array.isArray(raw["panes"])) {
    for (const p of raw["panes"]) {
      if (!isRecord(p) || typeof p["id"] !== "string" || !ID_RE.test(p["id"]) || p["id"] === MAIN_PANE)
        continue;
      if (panes.some((q) => q.id === p["id"]) || panes.length >= MAX_PANES) continue;
      const w = p["weight"];
      const pane: PaneState = {
        id: p["id"],
        weight: typeof w === "number" && w > 0 && Number.isFinite(w) ? w : 1,
      };
      if (p["collapsed"] === true) pane.collapsed = true;
      panes.push(pane);
    }
  }
  const instances: IndicatorInstance[] = [];
  const ids = new Set<string>();
  if (Array.isArray(raw["instances"])) {
    for (const x of raw["instances"]) {
      if (instances.length >= MAX_INDICATORS) break;
      if (!isRecord(x) || typeof x["id"] !== "string" || typeof x["type"] !== "string") continue;
      if (!ID_RE.test(x["id"]) || ids.has(x["id"])) continue;
      const def = registry.get(x["type"]);
      const rawParams = isRecord(x["params"]) ? x["params"] : {};
      let pane = typeof x["pane"] === "string" ? x["pane"] : MAIN_PANE;
      if (pane !== MAIN_PANE && !panes.some((p) => p.id === pane)) {
        if (def && !def.overlay && panes.length < MAX_PANES) {
          const fresh = { id: nextPaneId({ ...base, panes }), weight: 1 };
          panes.push(fresh);
          pane = fresh.id;
        } else pane = MAIN_PANE;
      }
      const inst: IndicatorInstance = {
        id: x["id"],
        type: x["type"],
        params: def ? resolveParams(def, rawParams) : { ...rawParams },
        pane,
        visible: x["visible"] !== false,
      };
      const style = sanitizeStyle(x["style"]);
      if (style) inst.style = style;
      const vis = sanitizeVisibility(x["visibility"]);
      if (vis) inst.visibility = vis;
      ids.add(inst.id);
      instances.push(inst);
    }
  }
  const mw = raw["mainWeight"];
  const state: ChartIndicatorsState = {
    v: 1,
    instances,
    panes,
    mainWeight: typeof mw === "number" && mw > 0 && Number.isFinite(mw) ? mw : DEFAULT_MAIN_WEIGHT,
    maximized: typeof raw["maximized"] === "string" ? raw["maximized"] : null,
  };
  if (raw["dataWindow"] === true) state.dataWindow = true;
  if (raw["legendCollapsed"] === true) state.legendCollapsed = true;
  return prunePanes(state);
}

// ------------------------------------------------------------------ templates

/** Snapshot the chart's indicators as a template (symbol/timeframe-agnostic). */
export function makeTemplate(name: string, state: ChartIndicatorsState): IndicatorTemplate {
  const { dataWindow: _dw, legendCollapsed: _lc, ...rest } = state;
  return {
    name: name.trim().slice(0, 64) || "Template",
    state: structuredClone({ ...rest, maximized: null }),
  };
}

/**
 * Apply a template: `replace` (TradingView's behaviour) swaps the chart's indicators for the
 * template's; `add` appends them with fresh ids (source refs and panes remapped).
 */
export function applyTemplate(
  state: ChartIndicatorsState,
  tpl: IndicatorTemplate,
  registry: IndicatorRegistry,
  mode: "replace" | "add" = "replace",
): ChartIndicatorsState {
  const incoming = sanitizeIndicatorsState(tpl.state, registry);
  if (mode === "replace") {
    const next = { ...incoming };
    if (state.dataWindow) next.dataWindow = true;
    if (state.legendCollapsed) next.legendCollapsed = true;
    return next;
  }
  let s = state;
  const idMap = new Map<string, string>();
  const paneMap = new Map<string, string>();
  for (const x of incoming.instances) {
    if (s.instances.length >= MAX_INDICATORS) break;
    const id = nextInstanceId(s, x.type);
    idMap.set(x.id, id);
    let pane: string = MAIN_PANE;
    if (x.pane !== MAIN_PANE) {
      const mapped = paneMap.get(x.pane);
      if (mapped) pane = mapped;
      else if (s.panes.length < MAX_PANES) {
        pane = nextPaneId(s);
        const src = incoming.panes.find((p) => p.id === x.pane);
        s = { ...s, panes: [...s.panes, { id: pane, weight: src?.weight ?? 1 }] };
        paneMap.set(x.pane, pane);
      }
    }
    s = { ...s, instances: [...s.instances, { ...structuredClone(x), id, pane }] };
  }
  // Re-point chained sources inside the added group.
  const instances = s.instances.map((x) => {
    if (![...idMap.values()].includes(x.id)) return x;
    const params: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(x.params)) {
      const r = typeof v === "string" ? parseSourceRef(v) : null;
      const to = r ? idMap.get(r.instanceId) : undefined;
      params[k] = r && to ? sourceRef(to, r.plotId) : v;
    }
    return { ...x, params };
  });
  return prunePanes({ ...s, instances });
}

// ------------------------------------------------------------------ synced preferences

/** Max saved templates (settings document size). */
export const MAX_TEMPLATES = 50;

/** Favourites from the settings document: known ids, deduplicated, order kept. */
export function sanitizeIndicatorFavorites(raw: unknown, registry: IndicatorRegistry): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) if (typeof v === "string" && registry.get(v) && !out.includes(v)) out.push(v);
  return out;
}

/** Most favourites kept (built-ins and Pine scripts). */
export const MAX_FAVORITES = 200;
const FAV_ID_RE = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * The stored favourite ids, well-formed and unique, **without** checking the registry: a Pine
 * script's id (`pine_<id>`) is registered only once "My scripts" has synced and compiled, and a
 * script with an error is not registered at all — its star must survive both (the dialog shows
 * what it can resolve; `sanitizeIndicatorFavorites` is the registry-checked view).
 */
export function sanitizeIndicatorFavoriteIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    if (out.length >= MAX_FAVORITES) break;
    if (typeof v === "string" && FAV_ID_RE.test(v) && !out.includes(v)) out.push(v);
  }
  return out;
}

export function toggleIndicatorFavorite(list: readonly string[], id: string): string[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/** Templates from the settings document: well-formed, unique names, states sanitised. */
export function sanitizeIndicatorTemplates(raw: unknown, registry: IndicatorRegistry): IndicatorTemplate[] {
  if (!Array.isArray(raw)) return [];
  const out: IndicatorTemplate[] = [];
  for (const t of raw) {
    if (!isRecord(t) || typeof t["name"] !== "string" || t["name"].trim() === "") continue;
    const name = t["name"];
    if (out.some((x) => x.name === name) || out.length >= MAX_TEMPLATES) continue;
    out.push({ name, state: sanitizeIndicatorsState(t["state"], registry) });
  }
  return out;
}

/** Save (or overwrite by name) a template, newest first. */
export function upsertTemplate(
  list: readonly IndicatorTemplate[],
  tpl: IndicatorTemplate,
): IndicatorTemplate[] {
  return [tpl, ...list.filter((t) => t.name !== tpl.name)].slice(0, MAX_TEMPLATES);
}
